import type { FC } from 'react';
import { useState } from 'react';
import type { BeneficialOwnerResponse } from '@gcv/shared';
import { BeneficialOwnerUpdateAction } from '@gcv/shared';

import { useObservedComponent } from 'hooks';
import { useCrbUpdateBeneficialOwnerArchiveStatus } from 'hooks/crb-beneficial-owners';
import { useInjection } from 'ioc';
import { CrbDispensaryStore } from 'stores/CrbDispensaryStore';
import { BeneficialOwnerDrawer } from 'ui/apps/shared/beneficial-owner/beneficial-owner-drawer';
import { getMostRecentCertification } from 'ui/apps/util/beneficial-owners';
import { CrbArchiveBeneficialOwnerModal } from './crb-archive-beneficial-owner-modal';
import { CrbBeneficialOwnerModal } from './crb-beneficial-owner-modal';

interface Props {
  open: boolean;
  onClose: () => void;
  owner: BeneficialOwnerResponse;
  questionnaireBankId?: string;
}

export const CrbBeneficialOwnerDrawer: FC<Props> = useObservedComponent(
  ({ open, onClose, owner, questionnaireBankId }) => {
    const dispensaryStore = useInjection(CrbDispensaryStore);

    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const { updateBeneficialOwnerArchiveStatus, isUpdatingBeneficialOwnerArchiveStatus } =
      useCrbUpdateBeneficialOwnerArchiveStatus(dispensaryStore.currentDispensary.id);
    const mostRecentCertification = getMostRecentCertification(owner);
    const questionnaireCertification =
      owner.last_update.update_action === BeneficialOwnerUpdateAction.Certify
        ? owner.last_update
        : mostRecentCertification;
    const questionnaireRoute = questionnaireCertification
      ? `/secure/crb/${dispensaryStore.currentDispensary.id}/questionnaires/${questionnaireCertification.template_id}/results/${questionnaireCertification.template_result_id}`
      : undefined;

    const handleArchiveOwnerClicked = () => {
      if (owner.archived) {
        updateBeneficialOwnerArchiveStatus({
          beneficialOwnerId: owner.id,
          archived: false,
          questionnaireBankId
        });
        return;
      }

      setIsArchiveModalOpen(true);
    };

    return (
      <>
        <BeneficialOwnerDrawer
          open={open && !isEditing}
          onClose={onClose}
          owner={owner}
          orgId={dispensaryStore.currentDispensary.id}
          orgName={dispensaryStore.currentDispensary.name}
          timezone={dispensaryStore.currentDispensary.iana_timezone}
          dataCy="crb-beneficial-owner-drawer"
          onEditOwner={() => setIsEditing(true)}
          onArchiveOwner={handleArchiveOwnerClicked}
          isArchivingOwner={isUpdatingBeneficialOwnerArchiveStatus}
          controlProngTooltip="Controlling/Managing Individual"
          questionnaireRoute={questionnaireRoute}
        />
        <CrbBeneficialOwnerModal
          isOpen={isEditing}
          onClose={() => setIsEditing(false)}
          questionnaireBankId={questionnaireBankId}
          config={{ addOrEdit: 'edit', owner: owner }}
        />
        <CrbArchiveBeneficialOwnerModal
          isOpen={isArchiveModalOpen}
          isArchiving={isUpdatingBeneficialOwnerArchiveStatus}
          owner={owner}
          onArchive={() =>
            updateBeneficialOwnerArchiveStatus(
              { beneficialOwnerId: owner.id, archived: true, questionnaireBankId },
              { onSuccess: () => setIsArchiveModalOpen(false) }
            )
          }
          onClose={() => setIsArchiveModalOpen(false)}
        />
      </>
    );
  }
);
